import { useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { Loader2 } from 'lucide-react';
import PublicNotFound from './PublicNotFound';
import { getPublicCaptive, readChilliParams, requestCaptiveCode, verifyCaptiveCode } from '@/api/captive';
import { CaptivePortalView } from '@/components/captive/CaptivePortalView';

/** my.linzametrics.com/wifi/<uuid> — гостевой портал филиала. Без входа. */
export default function WifiPortalPage() {
  const { publicId = '' } = useParams();
  // Роутер приводит гостя сюда со своими параметрами перехвата. Если их нет —
  // страницу просто открыли по ссылке или QR, и это нормальный режим.
  const { search } = useLocation();
  const uam = useMemo(() => readChilliParams(search), [search]);
  // Роутер вернул гостя сюда уже с открытой сетью: показываем «доступ открыт».
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const granted = params.get('granted') === '1';
  const continueUrl = params.get('next') || '';
  const { data, error, isLoading } = useSWR(
    publicId ? ['public-captive', publicId] : null,
    async () => (await getPublicCaptive(publicId)).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  useEffect(() => { if (data?.branch_name) document.title = data.branch_name; }, [data?.branch_name]);

  if (isLoading) return <div className="flex min-h-dvh items-center justify-center"><Loader2 className="size-6 animate-spin text-neutral-400" /></div>;
  if (error || !data) return <PublicNotFound />;
  // Прокрутка своя: html/body с overflow:hidden (globals.css), см. LeaveReviewPage.
  return (
    <div className="h-dvh overflow-y-auto">
      <CaptivePortalView
        identity={data.identity}
        branchName={data.branch_name}
        texts={data.texts}
        offers={data.offers}
        forceStep={granted ? 'done' : undefined}
        continueUrl={continueUrl}
        onRequestCode={async (phone) => { await requestCaptiveCode(publicId, phone, uam); }}
        onVerify={async (phone, code) => {
          const res = await verifyCaptiveCode(publicId, phone, code, uam);
          // Дальше сеть открывает роутер: возвращаем ему браузер с ответом на challenge.
          // Он же и покажет гостю страницу «доступ открыт» — обратно к нам.
          if (res.data.redirect_url) window.location.assign(res.data.redirect_url);
        }}
      />
    </div>
  );
}
